import React from 'react'
import { transitions } from 'blocks.app/config'
import { getThemeStyleValue } from 'theme/colors'
import { defaultThemeStyles } from './editText.theme'
import { noEmoji } from 'core/utils/displayUtil'
import type {
    EditTextDatePickerRef,
    EditTextInputElement,
    EditTextInputEvent,
    EditTextMod,
    EditTextProps,
    EditTextState,
} from './editText.types'

const hasInputTarget = (value: unknown): value is { target: EditTextInputElement } =>
    typeof value === 'object' && value !== null && 'target' in value
const themeStyles = (defaultThemeStyles as unknown) as Record<string, string>

type MaskedValueState = {
    value: string
    selection: { start: number; end: number } | null
}

const TIME_MASKS = ['99:99', '99:99:99']

const isTimeMask = (mask?: EditTextProps['mask']) => typeof mask === 'string' && TIME_MASKS.includes(mask)

const isValidTimePart = (value: string, maxValue: number) => {
    const tens = value.charAt(0)
    const units = value.charAt(1)

    if (/\d/.test(tens) && Number(tens) > Math.floor(maxValue / 10)) {
        return false
    }

    if (/\d/.test(tens) && /\d/.test(units)) {
        return Number(value) <= maxValue
    }

    return true
}

const isValidTime = (value: string) => {
    const [hours = '', minutes = '', seconds] = value.split(':')

    return (
        isValidTimePart(hours, 23) &&
        isValidTimePart(minutes, 59) &&
        (seconds === undefined || isValidTimePart(seconds, 59))
    )
}

const validateTimeBeforeMaskChange = (newState: MaskedValueState, oldState: MaskedValueState) =>
    isValidTime(newState.value) ? newState : oldState

export const getTimeMaskValidationProps = (mask?: EditTextProps['mask']) =>
    isTimeMask(mask) ? { beforeMaskedValueChange: validateTimeBeforeMaskChange } : {}

class EditTextMethods<Value = string> extends React.Component<EditTextProps<Value>, EditTextState> {
    inputFocus: EditTextInputElement | null = null
    datePicker: EditTextDatePickerRef | null = null
    timeout: ReturnType<typeof setTimeout> | null = null

    constructor(p_: EditTextProps<Value>) {
        super(p_)

        this.onClick = this.onClick.bind(this)
        this.onIconClick = this.onIconClick.bind(this)
        this.onIconMouseDown = this.onIconMouseDown.bind(this)
        this.onChange = this.onChange.bind(this)
        this.onChangeScreen = this.onChangeScreen.bind(this)
        this.onFocus = this.onFocus.bind(this)
        this.onBlur = this.onBlur.bind(this)
        this.onWheel = this.onWheel.bind(this)
    }
    state: EditTextState = {
        minValueError: false,
        maxValueError: false,
    }

    componentDidMount() {
        const p_ = this.props

        if (this.inputFocus) {
            this.inputFocus.addEventListener('wheel', this.onWheel, { passive: false })
        }

        if (p_.defaultFocus && this.inputFocus) {
            this.inputFocus.focus({ preventScroll: true })
        }

        if (p_.inputRef) {
            p_.inputRef(this.inputFocus)
        }
    }

    componentWillUnmount() {
        if (this.inputFocus) {
            this.inputFocus.removeEventListener('wheel', this.onWheel)
        }
    }

    validNumberKey = ['-']
    onIconMouseDown(e: React.MouseEvent<HTMLDivElement>) {
        // Prevent blur so parent onBlur does not cancel the clear action
        if (this.props.error) {
            e.preventDefault()
            e.stopPropagation()
            this.onIconClick(e)
        }
    }
    onIconClick(e: React.MouseEvent<HTMLDivElement>) {
        const p_ = this.props

        if (p_.error) {
            if (p_.onIconClick) {
                p_.onIconClick(e, this.inputFocus)
            } else if (p_.type === 'date') {
                p_.onChange?.((null as unknown) as Value, undefined)
            } else {
                p_.onChange?.(('' as unknown) as Value, undefined)
            }
            this.inputFocus?.focus({ preventScroll: true })
        } else if (p_.type === 'date' && !p_.onIconClick) {
            this.datePicker?.setOpen(true)
        } else if (p_.onIconClick) {
            p_.onIconClick(e, this.inputFocus)
        }
    }
    onClick(e: React.MouseEvent<EditTextInputElement>) {
        const p_ = this.props
        if (p_.onClick) {
            p_.onClick(e)
        }
    }
    onChangeScreen(e: unknown) {
        const p_ = this.props
        const value = hasInputTarget(e) ? e.target.value : null
        if (p_.onChange) {
            if (p_.type === 'date') {
                p_.onChange((e as unknown) as Value, undefined)
            } else {
                const numericValue = Number(value)
                const max = p_.max ?? 1920

                if (numericValue > 9 && numericValue <= max) {
                    this.setState({ maxValueError: false })
                    this.setState({ minValueError: false })
                    p_.onChange((value as unknown) as Value, hasInputTarget(e) ? e : undefined, max)
                } else {
                    this.setState({ maxValueError: true })
                    this.setState({ minValueError: true })
                    p_.onChange((value as unknown) as Value, hasInputTarget(e) ? e : undefined, false)
                }
            }
        }
        if (this.timeout) {
            clearTimeout(this.timeout)
        }
        this.timeout = setTimeout(() => {
            p_.onEndTyping?.((value as unknown) as Value | null)
        }, transitions.normal * 2)
    }
    onChange(e: unknown) {
        const p_ = this.props
        const value = hasInputTarget(e) ? e.target.value : null
        if (p_.onChange) {
            if (p_.type === 'date') {
                p_.onChange((e as unknown) as Value, undefined)
            } else if (value !== null) {
                if (p_.noSpaces && value.length > 0 && /^\s*$/.test(value)) {
                    p_.onChange(('' as unknown) as Value, e)
                } else {
                    p_.onChange(((!p_.enableEmoji ? noEmoji(value) : value) as unknown) as Value, e)
                }
            }
        }
        if (this.timeout) {
            clearTimeout(this.timeout)
        }
        this.timeout = setTimeout(() => {
            p_.onEndTyping?.((value as unknown) as Value | null)
        }, transitions.normal * 2)
    }
    onFocus(e: React.FocusEvent<EditTextInputElement>) {
        const p_ = this.props
        if (p_.onFocus) {
            if (p_.type === 'date') {
                p_.onFocus((e as unknown) as Value, e)
            } else {
                p_.onFocus((e.target.value as unknown) as Value, e)
            }
        }
    }
    onBlur(e: React.FocusEvent<EditTextInputElement>) {
        const p_ = this.props
        if (p_.onBlur) {
            if (p_.type === 'date') {
                p_.onBlur((e as unknown) as Value, e)
            } else {
                p_.onBlur((e.target.value as unknown) as Value, e)
            }
        }
    }
    onWheel(e: Event) {
        const wheelEvent = e as WheelEvent

        if (this.inputFocus !== document.activeElement) {
            return
        }

        if (this.props.disableWheel !== false) {
            e.preventDefault()
            return
        }

        if (wheelEvent.deltaY < 0) {
            const target = e.target as HTMLInputElement
            target.value = String(Number(target.value) - 1)
            this.onChange(e)

            return
        }
        if (wheelEvent.deltaY > 0) {
            const target = e.target as HTMLInputElement
            target.value = String(Number(target.value) + 1)
            this.onChange(e)

            return
        }
    }
    getIconColor(iconColor: string | undefined, mod: EditTextMod = 'default') {
        if (iconColor) {
            return iconColor
        } else {
            return getThemeStyleValue('ui', 'editText', `${mod}-icon`) || themeStyles[`${mod}-icon`]
        }
    }
    preventMinus = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.validNumberKey.includes(e.key)) {
            e.preventDefault()
        }
    }
}

export default EditTextMethods
